import { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Upload, Trash2, Plus, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { id: "partenaire", label: "Partenaires", desc: "Partenaires de l'agence et des événements" },
  { id: "sponsor", label: "Sponsors", desc: "Sponsors officiels" },
  { id: "club", label: "Clubs recruteurs", desc: "Clubs dont les recruteurs participeront — affichés en bande défilante sur l'accueil" },
];

export default function AdminLogos() {
  const [logos, setLogos] = useState([]);
  const [nom, setNom] = useState({});
  const [uploading, setUploading] = useState(null);
  const fileRefs = { partenaire: useRef(null), sponsor: useRef(null), club: useRef(null) };

  const load = useCallback(() => {
    axios.get(`${API}/admin/logos`, { withCredentials: true }).then((r) => setLogos(r.data)).catch(() => toast.error("Erreur de chargement des logos"));
  }, []);
  useEffect(load, [load]);

  const addLogo = async (categorie, file) => {
    if (!file) return;
    const name = (nom[categorie] || "").trim();
    if (!name) {
      toast.error("Entrez d'abord le nom");
      return;
    }
    setUploading(categorie);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/admin/upload`, fd, { withCredentials: true });
      await axios.post(`${API}/admin/logos`, { nom: name, categorie, image: data.url, ordre: logos.filter((l) => l.categorie === categorie).length }, { withCredentials: true });
      toast.success(`Logo « ${name} » ajouté`);
      setNom((n) => ({ ...n, [categorie]: "" }));
      load();
    } catch {
      toast.error("Échec de l'ajout");
    } finally {
      setUploading(null);
    }
  };

  const toggleActif = async (logo) => {
    try {
      await axios.put(`${API}/admin/logos/${logo.id}`, { nom: logo.nom, categorie: logo.categorie, image: logo.image, actif: !logo.actif, ordre: logo.ordre }, { withCredentials: true });
      load();
    } catch {
      toast.error("Erreur de mise à jour");
    }
  };

  const remove = async (logo) => {
    if (!window.confirm(`Retirer le logo « ${logo.nom} » ?`)) return;
    try {
      await axios.delete(`${API}/admin/logos/${logo.id}`, { withCredentials: true });
      toast.success("Logo retiré");
      load();
    } catch {
      toast.error("Erreur de suppression");
    }
  };

  return (
    <div data-testid="admin-logos" className="border border-[#27272A] bg-[#121214] p-6 space-y-8">
      <div>
        <p className="text-[10px] uppercase tracking-widest text-[#71717A]">Logos par catégorie</p>
        <p className="text-[11px] text-[#71717A] mt-2 leading-relaxed">
          Partenaires et sponsors s'affichent sur la page Événement. Les <span className="text-[#C5A059]">clubs recruteurs</span> défilent en bande animée sur la page d'accueil.
        </p>
      </div>
      {CATEGORIES.map((cat) => {
        const list = logos.filter((l) => l.categorie === cat.id);
        return (
          <div key={cat.id} data-testid={`logos-cat-${cat.id}`}>
            <p className="font-display uppercase font-bold text-sm">{cat.label} <span className="text-[#71717A] font-normal text-xs">({list.length})</span></p>
            <p className="text-[11px] text-[#71717A] mt-1 mb-4">{cat.desc}</p>
            <div className="flex flex-wrap items-center gap-4">
              {list.map((logo) => (
                <div key={logo.id} data-testid={`logo-item-${logo.id.slice(0, 8)}`} className={`relative group border p-3 bg-[#0A0A0C] ${logo.actif ? "border-[#27272A]" : "border-[#27272A] opacity-40"}`}>
                  <img src={logo.image} alt={logo.nom} className="h-12 w-auto object-contain" />
                  <p className="text-[10px] text-[#71717A] mt-2 text-center max-w-[100px] truncate">{logo.nom}</p>
                  <div className="absolute -top-2 -right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button onClick={() => toggleActif(logo)} data-testid={`logo-toggle-${logo.id.slice(0, 8)}`}
                      className="bg-[#27272A] text-white rounded-full p-1 text-[8px] uppercase hover:bg-[#C5A059] hover:text-black transition-colors" aria-label={logo.actif ? "Masquer" : "Afficher"} title={logo.actif ? "Masquer du site" : "Afficher sur le site"}>
                      {logo.actif ? <X size={11} /> : <Plus size={11} />}
                    </button>
                    <button onClick={() => remove(logo)} data-testid={`logo-delete-${logo.id.slice(0, 8)}`}
                      className="bg-red-500 text-white rounded-full p-1 hover:bg-red-400 transition-colors" aria-label="Supprimer">
                      <Trash2 size={11} />
                    </button>
                  </div>
                </div>
              ))}
              <div className="flex items-center gap-3 border border-dashed border-[#27272A] p-3">
                <input
                  value={nom[cat.id] || ""}
                  onChange={(e) => setNom((n) => ({ ...n, [cat.id]: e.target.value }))}
                  placeholder="Nom"
                  data-testid={`logo-nom-${cat.id}`}
                  className="w-32 bg-[#0A0A0C] border border-[#27272A] px-3 py-2 text-xs focus:outline-none focus:border-[#C5A059]"
                />
                <input ref={fileRefs[cat.id]} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" data-testid={`logo-file-${cat.id}`} onChange={(e) => { addLogo(cat.id, e.target.files[0]); e.target.value = ""; }} />
                <button type="button" onClick={() => fileRefs[cat.id].current?.click()} disabled={uploading === cat.id} data-testid={`logo-add-${cat.id}`}
                  className="flex items-center gap-2 border border-[#C5A059] text-[#C5A059] px-4 py-2 text-[10px] uppercase tracking-widest hover:bg-[#C5A059] hover:text-black transition-colors disabled:opacity-50">
                  {uploading === cat.id ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />} Logo
                </button>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
